import { trackProductAction } from '@/libs/product-analytics';
import { toaster } from '@/components/ui/toaster';
import axiosInstance from '@/libs/axiosInstance';
import type { ApiErrorResponse, ApiResponse } from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

interface DownloadStatementResponse {
  url: string;
}

interface DownloadStatementVariables {
  batchId: string;
  transferId: string;
}

interface UseDownloadPayoutStatementActionOptions {
  onSuccess?: (url: string) => void;
  onError?: (error: Error) => void;
}

/** The dashboard reads the statement in the brand's own languages. */
const DASHBOARD_LOCALES = ['en', 'fr'] as const;

const toStatementLocale = (language: string): (typeof DASHBOARD_LOCALES)[number] => {
  const base = language.split('-')[0] as (typeof DASHBOARD_LOCALES)[number];
  return DASHBOARD_LOCALES.includes(base) ? base : 'en';
};

/**
 * Fetches the signed link to one affiliate's payment statement and opens it.
 *
 * The statement is the account rendered for a single transfer, so it is keyed
 * by the transfer rather than by the batch it belongs to.
 */
export const useDownloadPayoutStatementAction = (
  options?: UseDownloadPayoutStatementActionOptions
) => {
  const { t, i18n } = useTranslation();

  const getErrorMessage = (error: AxiosError<ApiErrorResponse>): string => {
    const errorCode = error.response?.data?.code;

    // Try to translate based on error code (uses errors.{code} pattern)
    if (errorCode) {
      const translationKey = `errors.${errorCode}`;
      const translated = t(translationKey, { defaultValue: '' });
      if (translated && translated !== translationKey) {
        return translated;
      }
    }

    // Fall back to API message or generic error
    return error.response?.data?.message || t('payouts.statement.error.generic');
  };

  const mutation = useMutation({
    mutationFn: async ({ batchId, transferId }: DownloadStatementVariables) => {
      return trackProductAction('documents', 'download', async () => {
        const locale = toStatementLocale(i18n.language);
        const response = await axiosInstance.get<ApiResponse<DownloadStatementResponse>>(
          `/dashboard/payouts/${batchId}/transfers/${transferId}/statement?locale=${locale}`
        );
        return response.data;
      });
    },
    onSuccess: (response) => {
      const url = (response.data as DownloadStatementResponse).url;

      window.open(url, '_blank', 'noopener,noreferrer');

      toaster.create({
        title: t('payouts.statement.success.title'),
        description: t('payouts.statement.success.description'),
        type: 'success',
        duration: 5000,
      });

      options?.onSuccess?.(url);
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      console.error('Error downloading payout statement:', error);

      toaster.create({
        title: t('payouts.statement.error.title'),
        description: getErrorMessage(error),
        type: 'error',
        duration: 5000,
      });

      options?.onError?.(error);
    },
  });

  return {
    downloadStatement: mutation.mutate,
    downloadStatementAsync: mutation.mutateAsync,
    isDownloading: mutation.isPending,
    downloadingTransferId: mutation.isPending ? (mutation.variables?.transferId ?? null) : null,
  };
};
